import { useQueryClient } from '@tanstack/vue-query';
import { isAxiosError } from 'axios';
import type { RouteLocationNormalized } from 'vue-router';

import { profileQueryOptions } from '@/entities/profile';
import { resolveCurrentTeam } from '@/entities/team';
import { IS_BILLING_ENABLED } from '@/shared/config';
import { useTeamCookie } from '@/shared/hooks/use-team-cookie';

import { isFeatureAvailable } from './feature-guard';

const BILLING_BYPASS_ROUTES = new Set([
  'team/new',
  'team/billing',
  'team/invite',
  'access-restricted',
  'team',
  'team/settings',
]);

export const beforeEach = async (to: RouteLocationNormalized) => {
  const queryClient = useQueryClient();

  try {
    const isProfileNeeded = to.matched.some((record) => record.meta.profile);

    if (isProfileNeeded) {
      const profile = await queryClient.ensureQueryData(profileQueryOptions());

      if (!isFeatureAvailable(profile, to)) {
        return { name: '404' };
      }

      if (IS_BILLING_ENABLED && !BILLING_BYPASS_ROUTES.has(to.name as string)) {
        const { currentTeamUuid } = useTeamCookie();

        if (currentTeamUuid.value) {
          const team = await resolveCurrentTeam(queryClient, currentTeamUuid.value);

          // Stale team (deleted, or the user was removed): the cookie has been
          // cleared, so fall through to the personal account instead of trapping
          // the user — matches the no-team-cookie path below.
          if (team && !team.is_billing_ready) {
            const isOwner = team.members.some(
              (m) => m.uuid === profile.uuid && m.role === 'Owner',
            );

            return { name: isOwner ? 'team/billing' : 'access-restricted' };
          }
        }
      }
    }
  } catch (error) {
    // Only an auth failure should end the session.
    if (isAxiosError(error) && error.response?.status === 401) {
      queryClient.clear();
      return { name: 'login' };
    }

    if (isAxiosError(error) && error.response?.status === 404) {
      return { name: '404' };
    }

    return { name: '500' };
  }
};
